import React, { useState, useEffect } from "react";
import { 
  HiPencil, HiTrash, HiCheckCircle, HiPlus, HiSearch, 
  HiOutlineUserGroup, HiShieldExclamation, HiClock, 
  HiOutlineUserAdd, HiCalendar, HiUser, HiBuilding,
  HiOutlineArrowLeft, HiOutlineArrowRight,
  HiOfficeBuilding
} from "react-icons/hi";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import * as bootstrap from "bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { PulseLoader, ClipLoader } from "react-spinners";
import makeAnimated from 'react-select/animated';

const animatedComponents = makeAnimated();

const statusOptions = [
  { value: "Not Started", label: "Not Started", color: "#6c757d" },
  { value: "In Progress", label: "In Progress", color: "#0d6efd" },
  { value: "On Hold", label: "On Hold", color: "#ffc107" },
  { value: "Completed", label: "Completed", color: "#198754" },
  { value: "Cancelled", label: "Cancelled", color: "#dc3545" }
];

const ProjectManagement = () => {
  // State management
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [projectsPerPage, setProjectsPerPage] = useState(5);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [rolemanagement, setRoleManagement] = useState("");
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    inProgress: 0,
    onHold: 0,
    notStarted: 0,
    cancelled: 0
  });

  // Form states
  const [projectId, setProjectId] = useState(null);
  const [name, setName] = useState("");
  const [customer, setCustomer] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState("Not Started");
  const [teamMembers, setTeamMembers] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dropdown data
  const [customers, setCustomers] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [isFetchingDropdowns, setIsFetchingDropdowns] = useState(false);

  // Filter states
  const [customerFilter, setCustomerFilter] = useState("");
  const [developerFilter, setDeveloperFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Authorization header
  const getAuthHeader = () => ({
    headers: {
      Authorization: `Bearer ${sessionStorage.getItem("token")}`
    }
  });

  // Initialize component
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setRoleManagement(payload.role || "User");
      } catch (err) {
        console.error("Invalid token", err);
      }
    }
    
    fetchProjects();
    fetchCustomersAndDevelopers();
    fetchStats();
  }, []);

  // Fetch projects data
  const fetchProjects = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        "http://localhost:5890/getprojects",
        getAuthHeader()
      );
      setProjects(response.data);
      setFilteredProjects(response.data);
    } catch (err) {
      console.error("Error fetching projects:", err);
      toast.error("❌ Failed to fetch projects");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const response = await axios.get(
        "http://localhost:5890/projectstats",
        getAuthHeader()
      );
      setStats(response.data);
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  // Fetch dropdown data
  const fetchCustomersAndDevelopers = async () => {
    setIsFetchingDropdowns(true);
    try {
      // Fetch customers
      const customersResponse = await axios.get(
        "http://localhost:5890/GETMENSTIONAPI/Bussiness_all_details",
        getAuthHeader()
      );
      
      const customerOptions = customersResponse.data.customers?.map(c => ({
        value: c._id,
        label: c.lastName,
        email: c.email,
        company: c.company
      })) || [];
      setCustomers(customerOptions);
      
      // Fetch developers
      const developersResponse = await axios.get(
        "http://localhost:5890/adminperson/developers",
        getAuthHeader()
      );
      
      const developerOptions = developersResponse.data?.map(d => ({
        value: d._id || d.id,
        label: d.developerName || d.name,
        skills: d.skills || [],
        avatar: d.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(d.developerName || d.name)}&background=random`
      })) || [];
      setDevelopers(developerOptions);
      
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
      toast.error("Failed to load dropdown data");
    } finally {
      setIsFetchingDropdowns(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setProjectId(null);
    setName("");
    setCustomer("");
    setStartDate("");
    setEndDate("");
    setStatus("Not Started");
    setTeamMembers([]);
    setIsEditing(false);
    setIsSubmitting(false);
  };

  // Handle add or edit project
  const handleAddOrEditProject = async () => {
    if (!name) {
      toast.warn("⚠️ Project name is required");
      return;
    }

    // Validate dates
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      toast.warn("⚠️ End date must be after start date");
      return;
    }

    setIsSubmitting(true);

    // Prepare the project data
    const projectData = {
      name,
      customer: customer?.value || customer,
      startDate: startDate || null,
      endDate: endDate || null,
      status: status || "Not Started",
      teamMembers: teamMembers.map(member => ({
        developer: member.value || member.developer || member
      }))
    };

    try {
      if (isEditing) {
        // PUT request for editing existing project
        await axios.put(
          `http://localhost:5890/editproject/${projectId}`,
          projectData,
          getAuthHeader()
        );
        toast.success("🎉 Project updated successfully!");
      } else {
        // POST request for adding new project
        await axios.post(
          "http://localhost:5890/addproject",
          projectData,
          getAuthHeader()
        );
        toast.success("🎉 Project added successfully!");
      }
      
      // Close modal, reset form, and refresh data
      document.getElementById("closeModalBtn").click();
      resetForm();
      fetchProjects();
      fetchStats();
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.error || error.message || "Operation failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle edit project
  const handleEditProject = (project) => {
    setIsEditing(true);
    setProjectId(project._id);
    setName(project.name);
    
    // Set customer
    const customerValue = customers.find(c => c.value === project.customer) || project.customer;
    setCustomer(customerValue);
    
    // Format dates
    setStartDate(project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : "");
    setEndDate(project.endDate ? new Date(project.endDate).toISOString().split('T')[0] : "");
    setStatus(project.status || "Not Started");
    
    // Set team members
    const members = project.teamMembers?.map(member => {
      const dev = developers.find(d => d.value === member.developer_id || d.value === member.id);
      return dev || {
        value: member.developer_id || member.id,
        label: member.developerName || member.name || `Member ${member.developer_id || member.id}`,
        skills: member.skills || [],
        avatar: member.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.developerName || member.name || 'User')}&background=random`
      };
    }) || [];
    setTeamMembers(members);

    // Show modal
    const modal = new bootstrap.Modal(document.getElementById("addProjectModal"));
    modal.show();
  };

  // Delete project handlers
  const confirmDeleteProject = (project) => {
    setProjectToDelete(project);
    const modal = new bootstrap.Modal(document.getElementById("deleteConfirmationModal"));
    modal.show();
  };

  const handleDeleteProject = async () => {
    try {
      await axios.delete(
        `http://localhost:5890/deleteproject/${projectToDelete._id}`,
        getAuthHeader()
      );
      toast.success("🗑️ Project deleted successfully");
      fetchProjects();
      fetchStats();
      document.getElementById("closeDeleteModalBtn").click();
    } catch (err) {
      console.error("Error deleting project:", err);
      toast.error(err.response?.data?.error || "Failed to delete project");
    }
  };

  // Apply filters
  useEffect(() => {
    let filtered = projects;
    
    // Search filter
    if (search) {
      const searchTerm = search.toLowerCase();
      filtered = filtered.filter(project => {
        const customerObj = customers.find(c => c.value === project.customer);
        const customerName = customerObj?.label?.toLowerCase() || "";
        const customerCompany = customerObj?.company?.toLowerCase() || "";
        
        return (
          project.name?.toLowerCase().includes(searchTerm) ||
          customerName.includes(searchTerm) ||
          customerCompany.includes(searchTerm) ||
          project.status?.toLowerCase().includes(searchTerm)
        );
      });
    }
    
    // Customer filter
    if (customerFilter) {
      filtered = filtered.filter(project => project.customer === customerFilter.value);
    }
    
    // Developer filter
    if (developerFilter) {
      filtered = filtered.filter(project => 
        project.teamMembers?.some(member => 
          member.developer_id === developerFilter.value || 
          member.id === developerFilter.value
        )
      );
    }
    
    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(project => project.status === statusFilter);
    }
    
    setFilteredProjects(filtered);
    setCurrentPage(1);
  }, [search, customerFilter, developerFilter, statusFilter, projects, customers]);

  // Pagination calculations
  const indexOfLastProject = currentPage * projectsPerPage;
  const indexOfFirstProject = indexOfLastProject - projectsPerPage;
  const currentProjects = filteredProjects.slice(indexOfFirstProject, indexOfLastProject);
  const totalPages = Math.ceil(filteredProjects.length / projectsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Clear all filters
  const clearFilters = () => {
    setSearch("");
    setCustomerFilter("");
    setDeveloperFilter("");
    setStatusFilter("");
  };

  // Custom select styles
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      border: "1px solid #dee2e6",
      borderRadius: "0.375rem",
      minHeight: "42px",
      boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)" : "none",
      "&:hover": {
        borderColor: "#86b7fe",
      },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? "#0d6efd" : state.isFocused ? "#f8f9fa" : "white",
      color: state.isSelected ? "white" : "#212529",
      display: "flex",
      alignItems: "center",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#e9ecef",
      borderRadius: "4px",
    }),
    multiValueLabel: (base) => ({
      ...base,
      fontWeight: "500",
    }),
  };

  // Helper functions
  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption ? statusOption.color : "#6c757d";
  };

  const getCustomerName = (customerId) => {
    const customerObj = customers.find(c => c.value === customerId);
    return customerObj ? customerObj.label : customerId;
  };

  const getCustomerCompany = (customerId) => {
    const customerObj = customers.find(c => c.value === customerId);
    return customerObj?.company || "";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Custom components for Select
  const DeveloperOption = ({ innerProps, label, data }) => (
    <div {...innerProps} className="px-3 py-2 d-flex align-items-center">
      <img 
        src={data.avatar} 
        alt={label} 
        className="rounded-circle me-3" 
        width="32" 
        height="32"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(label)}&background=random`;
        }}
      />
      <div>
        <div className="fw-semibold">{label}</div>
        {data.skills && data.skills.length > 0 && (
          <small className="text-muted">
            {Array.isArray(data.skills) ? data.skills.join(', ') : data.skills}
          </small>
        )}
      </div>
    </div>
  );

  const MultiValue = ({ data, removeProps }) => (
    <div className="d-flex align-items-center bg-light rounded px-2 py-1 me-2">
      <img 
        src={data.avatar} 
        alt={data.label} 
        className="rounded-circle me-2" 
        width="24" 
        height="24"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(data.label)}&background=random`;
        }}
      />
      <span className="me-2">{data.label}</span>
      <button 
        {...removeProps} 
        className="btn btn-sm p-0 text-danger"
        style={{ lineHeight: 1, border: 'none', background: 'transparent' }}
      >
        &times;
      </button>
    </div>
  );

  return (
    <div className="container-fluid px-4 py-3">
      <ToastContainer 
        position="top-right" 
        autoClose={3000} 
        hideProgressBar={false}
        newestOnTop 
        closeOnClick 
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
      {/* Main Card */}
      <div className="card border-0 shadow-sm rounded-3 overflow-hidden">
        {/* Card Header */}
        <div className="card-header bg-white border-0 py-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3">
            <div className="d-flex align-items-center gap-3">
              <div className="bg-primary bg-opacity-10 p-3 rounded-3">
                <HiOutlineUserGroup size={28} className="text-primary" />
              </div>
              <div>
                <h5 className="mb-0 fw-semibold text-dark">Project Management</h5>
                <p className="text-muted mb-0 small">Manage all your projects in one place</p>
              </div>
            </div>
            
            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3">
              {/* Search */}
              <div className="position-relative" style={{ minWidth: "250px" }}>
                <HiSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={18} />
                <input
                  type="text"
                  className="form-control ps-5 py-2 rounded-pill border-0"
                  placeholder="Search projects..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ backgroundColor: "#f8f9fa" }}
                />
              </div>
              
              {/* Add Project Button */}
              {["Admin", "Manager"].includes(rolemanagement) && (
                <button
                  className="btn btn-primary d-flex align-items-center gap-2 px-4 py-2 rounded-pill shadow-sm"
                  data-bs-toggle="modal"
                  data-bs-target="#addProjectModal"
                  onClick={resetForm}
                >
                  <HiPlus size={18} />
                  Add Project
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="card-body border-bottom bg-light">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label small fw-semibold text-muted">Customer</label>
              <Select
                options={customers}
                value={customerFilter}
                onChange={setCustomerFilter}
                styles={customSelectStyles}
                placeholder="Filter by customer..."
                isClearable
                isLoading={isFetchingDropdowns}
                loadingMessage={() => "Loading customers..."}
                noOptionsMessage={() => "No customers found"}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label small fw-semibold text-muted">Developer</label>
              <Select
                options={developers}
                value={developerFilter}
                onChange={setDeveloperFilter}
                styles={customSelectStyles}
                placeholder="Filter by developer..."
                isClearable
                isLoading={isFetchingDropdowns}
                loadingMessage={() => "Loading developers..."}
                noOptionsMessage={() => "No developers found"}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label small fw-semibold text-muted">Status</label>
              <Select
                options={statusOptions}
                value={statusOptions.find(opt => opt.value === statusFilter)}
                onChange={(selected) => setStatusFilter(selected?.value || "")}
                styles={customSelectStyles}
                placeholder="Filter by status..."
                isClearable
              />
            </div>
            <div className="col-md-3 d-flex align-items-end">
              <button
                className="btn btn-outline-secondary w-100 rounded-pill"
                onClick={clearFilters}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="card-body pt-4">
          <div className="row g-3 mb-4">
            <div className="col-md-2">
              <div className="card border-0 shadow-sm h-100 rounded-3 bg-primary bg-opacity-10">
                <div className="card-body py-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-uppercase text-muted fw-semibold small">Total</h6>
                      <h3 className="mb-0 fw-bold">{stats.total}</h3>
                    </div>
                    <div className="bg-primary bg-opacity-25 p-2 rounded-circle">
                      <HiOutlineUserGroup size={20} className="text-primary" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card border-0 shadow-sm h-100 rounded-3 bg-success bg-opacity-10">
                <div className="card-body py-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-uppercase text-muted fw-semibold small">Completed</h6>
                      <h3 className="mb-0 fw-bold">{stats.completed}</h3>
                    </div>
                    <div className="bg-success bg-opacity-25 p-2 rounded-circle">
                      <HiCheckCircle size={20} className="text-success" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card border-0 shadow-sm h-100 rounded-3 bg-info bg-opacity-10">
                <div className="card-body py-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-uppercase text-muted fw-semibold small">In Progress</h6>
                      <h3 className="mb-0 fw-bold">{stats.inProgress}</h3>
                    </div>
                    <div className="bg-info bg-opacity-25 p-2 rounded-circle">
                      <HiClock size={20} className="text-info" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card border-0 shadow-sm h-100 rounded-3 bg-warning bg-opacity-10">
                <div className="card-body py-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-uppercase text-muted fw-semibold small">On Hold</h6>
                      <h3 className="mb-0 fw-bold">{stats.onHold}</h3>
                    </div>
                    <div className="bg-warning bg-opacity-25 p-2 rounded-circle">
                      <HiShieldExclamation size={20} className="text-warning" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card border-0 shadow-sm h-100 rounded-3 bg-secondary bg-opacity-10">
                <div className="card-body py-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-uppercase text-muted fw-semibold small">Not Started</h6>
                      <h3 className="mb-0 fw-bold">{stats.notStarted}</h3>
                    </div>
                    <div className="bg-secondary bg-opacity-25 p-2 rounded-circle">
                      <HiCalendar size={20} className="text-secondary" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card border-0 shadow-sm h-100 rounded-3 bg-danger bg-opacity-10">
                <div className="card-body py-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-uppercase text-muted fw-semibold small">Cancelled</h6>
                      <h3 className="mb-0 fw-bold">{stats.cancelled}</h3>
                    </div>
                    <div className="bg-danger bg-opacity-25 p-2 rounded-circle">
                      <HiTrash size={20} className="text-danger" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="card border-0 shadow-sm rounded-3 overflow-hidden">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="ps-4">Project Name</th>
                    <th>Customer</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Status</th>
                    <th>Team Size</th>
                    <th className="pe-4 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={7} className="text-center py-5">
                        <PulseLoader color="#0d6efd" size={10} />
                        <p className="mt-2 small text-muted">Loading projects...</p>
                      </td>
                    </tr>
                  ) : currentProjects.length > 0 ? (
                    currentProjects.map((project) => (
                      <tr key={project._id} className="border-top">
                        <td className="ps-4">
                          <div className="d-flex align-items-center gap-3">
                            <div className="bg-primary bg-opacity-10 p-2 rounded-2">
                              <HiOutlineUserGroup size={20} className="text-primary" />
                            </div>
                            <div>
                              <span className="fw-semibold d-block">{project.name}</span>
                              <small className="text-muted">
                                Created: {formatDate(project.createdAt)}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column">
                            <span className="fw-medium d-flex align-items-center gap-1">
                              <HiUser size={14} />
                              {getCustomerName(project.customer)}
                            </span>
                            {getCustomerCompany(project.customer) && (
                              <small className="text-muted d-flex align-items-center gap-1">
                                <HiOfficeBuilding size={12} />
                                {getCustomerCompany(project.customer)}
                              </small>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-1">
                            <HiCalendar size={14} className="text-muted" />
                            {formatDate(project.startDate)}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-1">
                            <HiCalendar size={14} className="text-muted" />
                            {formatDate(project.endDate)}
                          </div>
                        </td>
                        <td>
                          <span
                            className="badge rounded-pill fw-semibold px-3 py-2"
                            style={{ 
                              backgroundColor: `${getStatusColor(project.status)}15`,
                              color: getStatusColor(project.status),
                              border: `1px solid ${getStatusColor(project.status)}30`,
                              minWidth: '110px'
                            }}
                          >
                            {project.status}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <span className="fw-semibold">{project.teamMembers?.length || 0}</span>
                            <span className="text-muted">members</span>
                          </div>
                        </td>
                        <td className="pe-4 text-end">
                          <div className="d-flex gap-2 justify-content-end">
                            <button
                              className={`btn btn-sm btn-icon ${["Admin", "Manager"].includes(rolemanagement) ? "btn-outline-primary" : "btn-outline-secondary disabled"}`}
                              onClick={() => handleEditProject(project)}
                              disabled={!["Admin", "Manager"].includes(rolemanagement)}
                              data-bs-toggle="tooltip"
                              data-bs-title="Edit project"
                            >
                              <HiPencil size={16} />
                            </button>

                            <button
                              className={`btn btn-sm btn-icon ${rolemanagement === "Admin" ? "btn-outline-danger" : "btn-outline-secondary disabled"}`}
                              onClick={() => confirmDeleteProject(project)}
                              disabled={rolemanagement !== "Admin"}
                              data-bs-toggle="tooltip"
                              data-bs-title="Delete project"
                            >
                              <HiTrash size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-5">
                        <div className="py-4">
                          <div className="bg-light rounded-circle p-4 d-inline-block mb-3">
                            <HiOutlineUserGroup size={40} className="text-muted" />
                          </div>
                          <h5 className="fw-semibold">No projects found</h5>
                          <p className="text-muted mb-0">Try adjusting your filters or add a new project</p>
                          {filteredProjects.length === 0 && projects.length > 0 && (
                            <button
                              className="btn btn-link mt-2"
                              onClick={clearFilters}
                            >
                              Clear all filters
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {filteredProjects.length > 0 && (
              <div className="card-footer bg-white border-0 py-3">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center">
                  <div className="mb-2 mb-md-0 small text-muted">
                    Showing {indexOfFirstProject + 1} to {Math.min(indexOfLastProject, filteredProjects.length)} of {filteredProjects.length} entries
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    <div className="d-flex align-items-center">
                      <label htmlFor="perPageSelect" className="form-label mb-0 me-2 small">Show:</label>
                      <select
                        id="perPageSelect"
                        className="form-select form-select-sm"
                        style={{ width: '70px' }}
                        value={projectsPerPage}
                        onChange={(e) => {
                          setProjectsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                      >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                      </select>
                    </div>
                    <nav aria-label="Page navigation">
                      <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button 
                            className="page-link d-flex align-items-center gap-1" 
                            onClick={() => paginate(currentPage - 1)}
                          >
                            <HiOutlineArrowLeft size={14} />
                            Previous
                          </button>
                        </li>
                        {[...Array(Math.min(5, totalPages)).keys()].map(number => {
                          let pageNumber;
                          if (totalPages <= 5) {
                            pageNumber = number + 1;
                          } else if (currentPage <= 3) {
                            pageNumber = number + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNumber = totalPages - 4 + number;
                          } else {
                            pageNumber = currentPage - 2 + number;
                          }
                          
                          if (pageNumber < 1 || pageNumber > totalPages) return null;
                          
                          return (
                            <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                              <button className="page-link" onClick={() => paginate(pageNumber)}>
                                {pageNumber}
                              </button>
                            </li>
                          );
                        })}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button 
                            className="page-link d-flex align-items-center gap-1" 
                            onClick={() => paginate(currentPage + 1)}
                          >
                            Next
                            <HiOutlineArrowRight size={14} />
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Project Modal */}
      <div className="modal fade" id="addProjectModal" tabIndex="-1" aria-labelledby="addProjectModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content shadow-lg border-0 rounded-3">
            <div className="modal-header bg-primary text-white rounded-top-3">
              <h5 className="modal-title" id="addProjectModalLabel">
                {isEditing ? (
                  <>
                    <HiPencil className="me-2" />
                    Edit Project
                  </>
                ) : (
                  <>
                    <HiPlus className="me-2" />
                    Add New Project
                  </>
                )}
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
                id="closeModalBtn"
                onClick={resetForm}
              ></button>
            </div>

            <div className="modal-body px-4 py-4">
              {isFetchingDropdowns ? (
                <div className="text-center py-5">
                  <PulseLoader color="#0d6efd" size={10} />
                  <p className="mt-3 text-muted">Loading form data...</p>
                </div>
              ) : (
                <div className="row g-4">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      Project Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter project name"
                      disabled={isSubmitting}
                    />
                  </div>
                  
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      Customer
                    </label>
                    <Select
                      options={customers}
                      value={customer}
                      onChange={setCustomer}
                      styles={customSelectStyles}
                      placeholder="Select customer..."
                      isClearable
                      isLoading={isFetchingDropdowns}
                      loadingMessage={() => "Loading customers..."}
                      noOptionsMessage={() => "No customers found"}
                      isDisabled={isSubmitting}
                    />
                    {customer && customer.email && (
                      <small className="text-muted mt-2 d-block">
                        <HiUser size={12} className="me-1" />
                        {customer.email}
                      </small>
                    )}
                  </div>
                  
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      Start Date
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <HiCalendar size={16} />
                      </span>
                      <input
                        type="date"
                        className="form-control form-control-lg"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      End Date
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <HiCalendar size={16} />
                      </span>
                      <input
                        type="date"
                        className="form-control form-control-lg"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        min={startDate}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      Status <span className="text-danger">*</span>
                    </label>
                    <Select
                      options={statusOptions}
                      value={statusOptions.find(option => option.value === status)}
                      onChange={(selected) => setStatus(selected.value)}
                      styles={customSelectStyles}
                      placeholder="Select status..."
                      formatOptionLabel={(option) => (
                        <div className="d-flex align-items-center">
                          <span 
                            className="rounded-circle me-2" 
                            style={{
                              width: "10px", 
                              height: "10px", 
                              backgroundColor: option.color
                            }}
                          ></span>
                          {option.label}
                        </div>
                      )}
                      isDisabled={isSubmitting}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      Team Members
                    </label>
                    <Select
                      options={developers}
                      value={teamMembers}
                      onChange={setTeamMembers}
                      styles={customSelectStyles}
                      placeholder="Select team members..."
                      isMulti
                      isClearable
                      isLoading={isFetchingDropdowns}
                      loadingMessage={() => "Loading developers..."}
                      noOptionsMessage={() => "No developers found"}
                      components={{
                        Option: DeveloperOption,
                        MultiValue,
                        ...animatedComponents
                      }}
                      closeMenuOnSelect={false}
                      isDisabled={isSubmitting}
                    />
                    <small className="text-muted mt-2 d-block">
                      {teamMembers.length} developer(s) selected
                    </small>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer border-0 rounded-bottom-3 px-4 py-3 bg-light">
              <button
                type="button"
                className="btn btn-outline-secondary rounded-pill px-4"
                data-bs-dismiss="modal"
                disabled={isSubmitting}
                onClick={resetForm}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary rounded-pill px-4 d-flex align-items-center gap-2"
                onClick={handleAddOrEditProject}
                disabled={isSubmitting || !name}
              >
                {isSubmitting ? (
                  <>
                    <ClipLoader color="#fff" size={16} />
                    Processing...
                  </>
                ) : isEditing ? (
                  <>
                    <HiPencil size={16} />
                    Update Project
                  </>
                ) : (
                  <>
                    <HiPlus size={16} />
                    Add Project
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <div className="modal fade" id="deleteConfirmationModal" tabIndex="-1" aria-labelledby="deleteConfirmationModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg rounded-3">
            <div className="modal-header border-0 bg-danger text-white rounded-top-3">
              <h5 className="modal-title" id="deleteConfirmationModalLabel">
                <HiTrash className="me-2" />
                Confirm Deletion
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
                id="closeDeleteModalBtn"
              ></button>
            </div>
            <div className="modal-body px-4 py-4">
              {projectToDelete && (
                <>
                  <div className="d-flex align-items-center gap-3 mb-4">
                    <div className="bg-danger bg-opacity-10 p-3 rounded-2">
                      <HiTrash size={32} className="text-danger" />
                    </div>
                    <div>
                      <h6 className="fw-bold mb-1">{projectToDelete.name}</h6>
                      <div className="d-flex gap-3">
                        <small className="text-muted">
                          Status: <span className="fw-semibold">{projectToDelete.status}</span>
                        </small>
                        <small className="text-muted">
                          Team: <span className="fw-semibold">{projectToDelete.teamMembers?.length || 0} members</span>
                        </small>
                      </div>
                    </div>
                  </div>
                  <div className="alert alert-warning">
                    <div className="d-flex align-items-center gap-2">
                      <HiShieldExclamation size={20} />
                      <span className="fw-semibold">Warning: This action is permanent!</span>
                    </div>
                    <p className="mb-0 mt-2">
                      Are you sure you want to permanently delete <strong>{projectToDelete.name}</strong>? 
                      This action cannot be undone and all associated data will be lost.
                    </p>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer border-0 rounded-bottom-3 px-4 py-3">
              <button
                type="button"
                className="btn btn-outline-secondary rounded-pill px-4"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger rounded-pill px-4 d-flex align-items-center gap-2"
                onClick={handleDeleteProject}
              >
                <HiTrash size={16} />
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectManagement;